import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, Menu, X, Lock } from "lucide-react";
import { BUSINESS } from "@/lib/data";

const LINKS = [
  { id: "nav-link-services", label: "Services", href: "#services" },
  { id: "nav-link-maintenance", label: "Maintenance", href: "#maintenance" },
  { id: "nav-link-manifesto", label: "Craft", href: "#manifesto" },
  { id: "nav-link-areas", label: "Areas", href: "#areas" },
  { id: "nav-link-reviews", label: "Reviews", href: "#reviews" },
  { id: "nav-link-contact", label: "Contact", href: "#contact" },
];

export function scrollToHash(href) {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#0F1215]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-brand-logo"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 rounded-sm bg-[#E06A26] flex items-center justify-center transition-transform duration-500 group-hover:rotate-90">
            <Wrench className="w-4.5 h-4.5 text-[#0F1215]" strokeWidth={2.5} />
          </span>
          <span className="font-display font-800 tracking-[0.18em] text-lg font-extrabold">
            THOR<span className="text-[#E06A26]">TEK</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              onClick={(e) => go(e, l.href)}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#94A0AC] hover:text-[#F4F2EE] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/admin/login"
            data-testid="nav-btn-admin-login"
            className="text-[#606D7A] hover:text-[#94A0AC] transition-colors duration-300"
            aria-label="Admin login"
          >
            <Lock className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-btn-book-now"
            onClick={(e) => go(e, "#contact")}
            className="bg-[#E06A26] hover:bg-[#F07A34] text-[#0F1215] font-semibold text-sm px-6 py-2.5 rounded-full transition-all duration-300 hover:shadow-[0_0_28px_rgba(224,106,38,0.45)]"
          >
            Book a Repair
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-btn"
          className="lg:hidden text-[#F4F2EE]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-[#0F1215]/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  onClick={(e) => go(e, l.href)}
                  className="font-mono text-xs uppercase tracking-[0.22em] text-[#94A0AC]"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-btn-book-now-mobile"
                onClick={(e) => go(e, "#contact")}
                className="bg-[#E06A26] text-[#0F1215] font-semibold text-sm px-6 py-3 rounded-full w-fit"
              >
                Book a Repair
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
